export const fontSize = {
  xs: '16px',
  sm: '22px',
  base: '26px',
  lg: '32px',
  xl: '40px',
  '2xl': '52px',
  '3xl': '64px'
};

export const lineHeight = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2
};

export const whiteSpace = {
  '0': '0',
  '1': '0.25rem',
  '2': '0.5rem',
  '3': '0.75rem',
  '4': '1rem',
  '5': '1.25rem',
  '6': '1.5rem',
  '8': '2rem',
  '10': '2.5rem',
  '12': '3rem',
  '14': '3.5rem',
  '16': '4rem',
  '20': '5rem',
  '22': '5.5rem',
  '24': '6rem',
  '32': '8rem',
  '40': '10rem',
  '48': '12rem',
  '56': '14rem',
  '64': '16rem'
};

const COLOR_KEYS = [
  'cta',
  'cta200',
  'cta300',
  'cta400',
  'cta500',
  'cta600',
  'ui100',
  'ui200',
  'ui300',
  'ui400',
  'ui500',
  'ui600',
  'ui700',
  'ui800',
  'ui900',
  'error',
  'error300',
  'warning',
  'success',
  'info',
  'white',
  'black',
  'imdb',
  'facebook',
  'backdrop'
] as const;

export type ColorKey = typeof COLOR_KEYS[number];

const cssVarName = (key: ColorKey): string =>
  `--${key.replace(/(\d+)/, '-$1')}`;

export const themeColor = (key: ColorKey, opacity?: number): string => {
  const ref = `var(${cssVarName(key)})`;
  return opacity === undefined ? `rgb(${ref})` : `rgb(${ref} / ${opacity})`;
};

export const colors = Object.fromEntries(
  COLOR_KEYS.map((k) => [k, themeColor(k)])
) as { [K in ColorKey]: string };

export type Theme = {
  color: { [K in ColorKey]: string };
  font: { roboto: string; robotoDin: string };
  size: {
    super: string;
    large: string;
    title: string;
    text: string;
    tinyTitle: string;
    infoText: string;
    tinyInfo: string;
  };
  weight: {
    bold: number;
    medium: number;
    regular: number;
    light: number;
  };
  letterSpacing: { big: string; small: string };
  lineHeight: { small: string };
  state: { disabledOpacity: number };
};

export const darkTheme: Theme = {
  color: colors,
  font: {
    roboto: 'Roboto, sans-serif',
    robotoDin: 'RobotoDin, sans-serif'
  },
  size: {
    super: '2.5rem',
    large: '1.5rem',
    title: '1.25rem',
    text: '1rem',
    tinyTitle: '0.8125rem',
    infoText: '0.8125rem',
    tinyInfo: '0.625rem'
  },
  weight: {
    bold: 700,
    medium: 500,
    regular: 400,
    light: 300
  },
  letterSpacing: { big: '1px', small: '0' },
  lineHeight: { small: '17px' },
  state: { disabledOpacity: 0.5 }
};
